import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AceLanguage, NotebookCellFacade, NotebookTypes } from '@mode/notebooks/contract';
import { map, Observable, Subject, takeUntil, of } from 'rxjs';
import { NormalizedNotebookCell, SessionResource } from '@mode/notebooks/contract';
import * as LegacyNotebookSelectors from '@mode/notebooks/domain';
import { Store } from '@ngrx/store';
import { dispatchEvent } from '@mode/shared/util-js';
import { NotebookSecretService } from '@mode/notebooks/domain';

@Component({
  selector: 'mode-notebook-editor-cell',
  templateUrl: './notebook-editor-cell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotebookEditorCellComponent implements OnInit, OnChanges {
  @Input() cell!: NormalizedNotebookCell;
  @Input() notebookToken!: string;
  @Input() session?: SessionResource;
  @Input() readOnly!: boolean;
  @Input() showInLayout!: boolean;

  cellContent!: string;
  markdownText!: string;
  aceControls: any = {};
  destroyed$ = new Subject<void>();

  executionCount$!: Observable<string>;
  isEditing$!: Observable<boolean>;
  secretKeys$!: Observable<string[]>;
  isMarkdownViewMode$!: Observable<boolean>;
  lang$!: Observable<AceLanguage>;
  typeAbbr$!: Observable<'Md' | 'Py' | 'R'>;

  constructor(
    private notebookCellFacade: NotebookCellFacade,
    private notebookSecretService: NotebookSecretService,
    private store: Store,
    private window: Window
  ) {}

  ngOnInit(): void {
    this.isEditing$ = this.store.select(LegacyNotebookSelectors.createIsCellEditingSelector(this.cell.token));
    this.secretKeys$ = this.notebookSecretService.secretKeys$;

    this.executionCount$ = (
      this.cell ? this.store.select(LegacyNotebookSelectors.createCellExecutionCountSelector(this.cell.token)) : of(0)
    ).pipe(map((count) => `[${count || '_'}]`));

    this.isMarkdownViewMode$ = this.store.select(
      LegacyNotebookSelectors.createMarkdownViewModeSelector(this.cell.token)
    );

    this.lang$ = this.store.select(LegacyNotebookSelectors.createCellAceLangSelector(this.cell.token));

    this.typeAbbr$ = this.lang$.pipe(
      map((t) => {
        switch (t) {
          case AceLanguage.Markdown:
            return 'Md';
          case AceLanguage.Python:
            return 'Py';
          case AceLanguage.R:
            return 'R';
          default:
            return 'Py';
        }
      })
    );

    this.cellContent = this.cell?.content;

    this.isEditing$.pipe(takeUntil(this.destroyed$)).subscribe((editing) => {
      if (editing) {
        setTimeout(() => {
          this.aceControls.focus?.();
          dispatchEvent(this.window, 'resize');
        });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cell']) {
      this.cellContent = this.cell.content;
      this.markdownText = (this.cellContent && this.cellContent.trim()) || 'Markdown text here...';
    }
  }

  handleTextChanged(text: Event): void {
    this.cellContent = (text as any).$text;
    this.notebookCellFacade.editCellContent(this.cellContent, this.cell.token);
  }

  toggleContentType(): void {
    const newType =
      this.cell.content_type === NotebookTypes.ContentType.Python
        ? NotebookTypes.ContentType.Markdown
        : NotebookTypes.ContentType.Python;
    this.notebookCellFacade.updateContentType(this.cell.token, newType);
  }

  editMarkdown(): void {
    if (!this.cell.frozen && !this.readOnly) {
      this.notebookCellFacade.beginEditCell(this.cell.token);
    }
  }

  handleEditorClick(event: MouseEvent): void {
    const clickedInside = (event.target as HTMLElement).closest('mode-notebook-cell-ace');
    if (!clickedInside && this.cell.content_type === 'python') {
      this.editMarkdown();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
